import { SEARCH_RESULT_GROUP_ORDER, type GlobalSearchResult } from '@erp/shared';
import { Clock3, Search } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { cn } from '@/lib/utils';
import { useGlobalSearch, useRecordRecentSearch } from '../api/use-global-search';

function highlight(value: string, query: string): JSX.Element {
  const tokens = query
    .trim()
    .split(/\s+/u)
    .filter((token) => token.length > 1)
    .map((token) => token.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&'));
  if (tokens.length === 0) return <>{value}</>;
  const expression = new RegExp(`(${tokens.join('|')})`, 'giu');
  const exactMatch = new RegExp(`^(?:${tokens.join('|')})$`, 'iu');
  return (
    <>
      {value.split(expression).map((part, index) =>
        exactMatch.test(part) ? (
          <mark
            key={`${part}-${String(index)}`}
            className="rounded-sm bg-primary/15 text-foreground"
          >
            {part}
          </mark>
        ) : (
          <Fragment key={`${part}-${String(index)}`}>{part}</Fragment>
        ),
      )}
    </>
  );
}

export function GlobalSearch(): JSX.Element | null {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const search = useGlobalSearch({ query: debouncedQuery, enabled: open });
  const recordRecent = useRecordRecentSearch();

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 180);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    const listener = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openSearch') !== '1') return;
    setOpen(true);
    params.delete('openSearch');
    void navigate(
      { pathname: location.pathname, search: params.size === 0 ? '' : `?${params.toString()}` },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => setActiveIndex(0), [debouncedQuery, search.data]);

  const grouped = useMemo(() => {
    const data = search.data?.data ?? [];
    return SEARCH_RESULT_GROUP_ORDER.map((entityType) => ({
      entityType,
      results: data.filter((result) => result.entityType === entityType),
    })).filter((group) => group.results.length > 0);
  }, [search.data]);
  const flatResults = grouped.flatMap((group) => group.results);

  if (!can('global_search:use')) return null;

  const openResult = (result: GlobalSearchResult): void => {
    recordRecent.mutate(result.searchIndexId);
    setOpen(false);
    navigate(result.route);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(0, flatResults.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(0, current - 1));
    } else if (event.key === 'Enter') {
      const result = flatResults[activeIndex];
      if (result !== undefined) {
        event.preventDefault();
        openResult(result);
      }
    }
  };

  let resultIndex = -1;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-11 min-w-11 shrink-0 gap-2 px-3 md:h-11 xl:w-56 xl:justify-start"
        aria-label={t('search.open')}
        aria-keyshortcuts="Control+K Meta+K"
        onClick={() => setOpen(true)}
      >
        <Search className="h-5 w-5" aria-hidden="true" />
        <span className="hidden truncate text-muted-foreground xl:inline">
          {t('search.placeholder')}
        </span>
        <kbd className="ms-auto hidden rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground xl:inline">
          {t('search.shortcut')}
        </kbd>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          closeLabel={t('common.close')}
          className="top-[max(1rem,env(safe-area-inset-top))] max-h-[calc(100dvh-2rem)] max-w-2xl translate-y-0 gap-0 overflow-hidden p-0 sm:top-[12vh]"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
            inputRef.current?.select();
          }}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{t('search.title')}</DialogTitle>
            <DialogDescription>{t('search.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 border-b border-border px-3 pe-12">
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.placeholder')}
              className="h-14 border-0 px-0 shadow-none focus-visible:ring-0"
              role="combobox"
              aria-expanded={flatResults.length > 0}
              aria-controls="global-search-results"
              aria-activedescendant={
                flatResults[activeIndex] === undefined
                  ? undefined
                  : `search-result-${flatResults[activeIndex].searchIndexId}`
              }
              aria-autocomplete="list"
            />
          </div>
          <div
            id="global-search-results"
            role="listbox"
            className="max-h-[min(70dvh,34rem)] overflow-y-auto p-2"
          >
            {search.isPending ? (
              <div className="space-y-2 p-2" aria-label={t('search.loading')}>
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-4/5" />
              </div>
            ) : search.isError ? (
              <div className="p-6 text-center" role="alert">
                <p className="text-sm font-medium">{t('search.error')}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {search.error.trackingId ?? search.error.message}
                </p>
                <Button
                  className="mt-3"
                  variant="outline"
                  size="sm"
                  onClick={() => void search.refetch()}
                >
                  {t('common.retry')}
                </Button>
              </div>
            ) : flatResults.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {debouncedQuery.length === 0 ? t('search.noRecent') : t('search.empty')}
              </div>
            ) : (
              grouped.map((group) => (
                <section
                  key={group.entityType}
                  aria-labelledby={`search-group-${group.entityType}`}
                >
                  <h3
                    id={`search-group-${group.entityType}`}
                    className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {t(`search.groups.${group.entityType}`)}
                  </h3>
                  <div className="space-y-1">
                    {group.results.map((result) => {
                      resultIndex += 1;
                      const index = resultIndex;
                      return (
                        <button
                          id={`search-result-${result.searchIndexId}`}
                          key={result.searchIndexId}
                          type="button"
                          role="option"
                          aria-selected={activeIndex === index}
                          className={cn(
                            'flex min-h-12 w-full items-center gap-3 rounded-md px-3 py-2 text-start outline-none',
                            activeIndex === index
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-accent/60',
                          )}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => openResult(result)}
                        >
                          {result.isRecent ? (
                            <Clock3
                              className="h-4 w-4 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          ) : (
                            <Search
                              className="h-4 w-4 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              {highlight(result.title, debouncedQuery)}
                            </span>
                            {result.subtitle === null ? null : (
                              <span className="block truncate text-xs text-muted-foreground">
                                {highlight(result.subtitle, debouncedQuery)}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
          </div>
          <div
            className="border-t border-border px-3 py-2 text-xs text-muted-foreground"
            aria-live="polite"
          >
            {t('search.resultCount', { count: search.data?.meta.total ?? 0 })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
